import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Skeleton, Tooltip, message } from 'antd';
import { ShoppingCart, Minus, Plus } from 'feather-icons-react/build/IconComponents';

const ProductList = ({
  products,
  activeTab,
  getCategoryName,
  getBrandName,
  loading,
  error,
  handleDragStart,
  handleDragEnd,
  handleAddToOrder,
}) => {
  const [quantities, setQuantities] = useState({});

  // Products are already filtered in the parent component
  const validProducts = useMemo(() => {
    return products.filter(p => Number(p.price) > 0);
  }, [products]);

  const calculateFinalPrice = (product) => {
    let finalPrice = Number(product.price ?? 0);
    const discountType = product.discount_type;
    const discountValue = Number(product.discount_value ?? 0);

    if (discountType === 'percentage' && discountValue > 0) {
      finalPrice *= (1 - discountValue / 100);
    } else if (discountType === 'fixed' && discountValue > 0) {
      finalPrice -= discountValue;
    }
    return Math.max(finalPrice, 0);
  };

  const handleQuantityChange = (productId, delta) => {
    setQuantities(prev => ({
      ...prev,
      [productId]: Math.max(1, (prev[productId] || 1) + delta),
    }));
  };

  const handleAddClick = (product) => {
    const quantity = Number(quantities[product.id]) || 1;
    const finalPrice = calculateFinalPrice(product);

    try {
      handleAddToOrder(
        { ...product, discountedPrice: finalPrice },
        quantity
      );
      setQuantities(prev => ({ ...prev, [product.id]: 1 }));
    } catch (err) {
      message.error('Failed to add product to order');
    }
  };

  const getDisplayImage = (product) => {
    return Array.isArray(product.image) && product.image.length > 0
      ? product.image[0]
      : '/assets/img/products/pos-product-01.png';
  };

  if (loading) {
    return (
      <div className="row g-3">
        {Array.from({ length: 8 }).map((_, index) => (
          <div key={index} className="col-sm-6 col-md-6 col-lg-6 col-xl-4 col-xxl-3">
            <Skeleton.Image style={{ width: '100%', height: 150, marginBottom: 8 }} active />
            <Skeleton active paragraph={{ rows: 2 }} />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return <p className="text-danger">Error: {error}</p>;
  }

  return (
    <div className="tab_content active" data-tab={activeTab}>
      <div className="row g-3">
        {validProducts.length === 0 ? (
          <p>No products found for this category.</p>
        ) : (
          validProducts.map(product => {
            const originalPrice = Number(product.price ?? 0);
            const finalPrice = calculateFinalPrice(product);
            const displayImage = getDisplayImage(product);

            return (
              <div
                key={product.id}
                className="col-sm-6 col-md-6 col-lg-6 col-xl-4 col-xxl-3"
                draggable
                onDragStart={(e) => handleDragStart(e, { ...product, discountedPrice: finalPrice })}
                onDragEnd={handleDragEnd}
              >
                <div className="product-info card mb-0" style={{ height: '300px', width: '100%', display: 'flex', flexDirection: 'column' }}>
                  {product.discount_type && product.discount_value > 0 && (
                    <div className="discount-badge">
                      {product.discount_type === 'percentage'
                        ? `-${product.discount_value}%`
                        : `-$${product.discount_value}`}
                    </div>
                  )}

                  <Tooltip title="Add to Order" placement="top">
                    <button
                      type="button"
                      className="custom-btn-cart"
                      onClick={() => handleAddClick(product)}
                    >
                      <ShoppingCart size={16} />
                    </button>
                  </Tooltip>

                  <div className="pro-img" style={{ flex: '1', overflow: 'hidden' }}>
                    <img
                      src={displayImage}
                      onError={(e) => (e.target.src = '/assets/img/products/pos-product-01.png')}
                      alt={product.name}
                      style={{ width: '100%', height: '150px', objectFit: 'cover' }}
                    />
                  </div>

                  <div className="d-flex justify-content-between" style={{ flex: '0 0 auto' }}>
                    <h6 className="cat-name text-muted mt-2" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '13px' }}>
                      {getCategoryName(product.category_id)}
                    </h6>
                    {product.brand_id && (
                      <h6 className="brand-name text-muted mt-2" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '13px' }}>

                        {getBrandName(product.brand_id)}
                      </h6>
                    )}
                  </div>
                  <h6 className="product-name fw-semibold" style={{ flex: '0 0 auto', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {product.name}
                  </h6>

                  <div className="d-flex justify-content-between align-items-center price" style={{ flex: '0 0 auto' }}>
                    <p className="mb-0">
                      {product.discount_type && product.discount_value > 0 ? (
                        <>
                          <span className="text-decoration-line-through text-muted">
                            ${originalPrice.toFixed(2)}
                          </span>{' '}
                          <span className="text-success fw-bold">${finalPrice.toFixed(2)}</span>
                        </>
                      ) : (
                        <span className="fw-medium text-gray-9 fw-bold">
                          ${originalPrice.toFixed(2)}
                        </span>
                      )}
                    </p>

                    <div className="qty-item">
                      <a
                        href="#"
                        className="dec d-flex justify-content-center align-items-center"
                        onClick={() => handleQuantityChange(product.id, -1)}
                      >
                        <Minus size={14} />
                      </a>
                      <input
                        type="text"
                        className="form-control text-center"
                        name="qty"
                        value={quantities[product.id] || 1}
                        readOnly
                      />
                      <a
                        href="#"
                        className="inc d-flex justify-content-center align-items-center"
                        onClick={() => handleQuantityChange(product.id, 1)}
                      >
                        <Plus size={14} />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

ProductList.propTypes = {
  products: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    image: PropTypes.arrayOf(PropTypes.string),
    category_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    brand_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    is_variant: PropTypes.bool,
    variant_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    discount_type: PropTypes.string,
    discount_value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  })).isRequired,
  activeTab: PropTypes.string.isRequired,
  getCategoryName: PropTypes.func.isRequired,
  getBrandName: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  error: PropTypes.string,
  handleDragStart: PropTypes.func,
  handleDragEnd: PropTypes.func,
  handleAddToOrder: PropTypes.func.isRequired,
};

ProductList.defaultProps = {
  loading: false,
  error: null,
  handleDragStart: () => { },
  handleDragEnd: () => { },
};

export default ProductList;
